import React, {SyntheticEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {KeySnapshot, KeyTransaction} from '../../api/Model';
import {KeyVaultSelect} from '../../components/key-vault-select/KeyVaultSelect';
import {isoDate, today, WeekDay, withDayOfWeek} from '../../util/DateUtil';
import {useRemoteValue} from '../../util/RemoteValue';
import {
    inputChange,
    inputChangeEvent,
    mapSetState,
    selectChangeEvent,
    SetState,
    toggleButtonEvent
} from '../../util/FormHandlers';
import {EmployeeKeyHistory, useEmployeeKeyHistoryStore} from "../../api/EmployeeKeyHistoryStore";
import {DateField} from "../../components/date-field/DateField";
import {EmployeeSelect} from "../../components/employee-select/EmployeeSelect";
import {EmployeeCardMedia} from "../../components/employee-avatar/EmployeeCardMedia";
import {
    getAvailableActions,
    MissingKeyReason,
    missingKeyReasons,
    noopAction,
    SelectPersonActionPayload,
    SelectVaultActionPayload,
    ValidationError,
    WizardKeyAction
} from "./WizardState";
import {CompanySelect} from "../../components/company-select/CompanySelect";
import {useAuthStore} from "../../app-layout/authStore";
import {useMessage} from "../../util/actions/Message";
import {ControlWithProgress} from "../../components/web-socket/ControlWithProgress";
import {apiErrorMessage} from "../../util/ApiErrorMessage";
import {createDateFieldAdornment} from "../../components/date-field/DateFieldAdornment";
import {KeyTransactionRecord, useKeyTransactionStore} from "../../api/KeyTransactionStore";
import JetBrainsIcon from "../../components/icons/JetBrainsIcon";
import {KeyLocationShield} from "../../components/location/KeyLocationShield";
import {KeyChip} from "../../components/key-chip/KeyChip";
import {KeyHistoryPeriod} from "../../components/key-history-period/KeyHistoryPeriod";
import ContractorIcon from '@mui/icons-material/Engineering';
import {SnackbarKey, useSnackbar} from "notistack";


function ActionHeader({text, children}: {
    text: React.ReactNode,
    children?: React.ReactNode,
}) {
    return (
        <Stack direction="row" sx={{minHeight: '42px'}} alignItems="center" spacing={2}>
            <Typography variant="h6">{text}</Typography>
            {children}
        </Stack>
    );
}

function KeyCurrentStatePanel({keySnapshot}: {
    keySnapshot: KeySnapshot,
}) {
    const {key, historyRecord} = keySnapshot;

    return (
        <Stack
            direction="column"
            gap={1}
            sx={{
                mb: 2,
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'action.hover',
            }}
        >
            <Typography variant="h5" gutterBottom>Current Key Status</Typography>
            <Stack direction="row" alignItems="center" gap={4} flexWrap="wrap" sx={{mb: 2}}>
                <KeyChip keyData={key} record={historyRecord} size="medium"/>
                <KeyLocationShield location={historyRecord.location}/>
                <KeyHistoryPeriod record={historyRecord}/>
                {historyRecord.notes && (
                    <Typography variant="body2" color="text.secondary">
                        {historyRecord.notes}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

function KeyReasonSelect({employeeKey, reason, setReason}: {
    employeeKey: KeySnapshot,
    reason?: MissingKeyReason,
    setReason: (employeeKey: KeySnapshot, reason: MissingKeyReason) => void,
}) {
    const handleReasonChange = useMemo(
        () => selectChangeEvent<MissingKeyReason['key']>(newReasonKey => {
            const newReason = missingKeyReasons
                .find(reason => reason.key === newReasonKey)
            if (newReason) {
                setReason(employeeKey, newReason);
            }
        }),
        [employeeKey, setReason],
    );

    const label = `What happened to key ${employeeKey.key.keyNumber} (${employeeKey.historyRecord.vault.name})?`;
    return (
        <FormControl required fullWidth>
            <InputLabel id="reason-label">{label}</InputLabel>
            <Select
                labelId="reason-label"
                label={label}
                value={reason?.key || ''}
                onChange={handleReasonChange}
                variant="outlined"
            >
                {missingKeyReasons.map(option => (
                    <MenuItem key={option.key} value={option.key}>{option.text}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

function SelectPersonSubpanel({payload, setPayload}: {
    payload: SelectPersonActionPayload,
    setPayload: SetState<SelectPersonActionPayload>,
}) {
    const employeeKeyHistoryStore = useEmployeeKeyHistoryStore();
    const employeeKeyHistory = useRemoteValue(employeeKeyHistoryStore.remoteValue, new EmployeeKeyHistory([]));

    const handlers = useMemo(() => ({
        handlePersonTypeChange: toggleButtonEvent(newValue => {
            setPayload({
                ...payload,
                personType: newValue as 'employee' | 'guest',
                employee: undefined,
                guestFullName: '',
                keyReasons: [],
            })
        }),
        setEmployee: mapSetState(setPayload, 'employee'),
        handleGuestFullNameChange: inputChange(setPayload, newValue => ({guestFullName: newValue})),
        setGuestCompany: mapSetState(setPayload, 'guestCompany'),
        handleIssuanceTypeChange: toggleButtonEvent((newValue: SelectPersonActionPayload['issuanceType'] | null) => {
            if (!newValue) return;
            setPayload(prevPayload => ({
                ...prevPayload,
                issuanceType: newValue,
                expToDate: newValue === 'permanent' ? undefined : prevPayload.expToDate,
            }));
        }),
        setExpToDate: mapSetState(setPayload, 'expToDate'),
        setReason: (employeeKey: KeySnapshot, reason: MissingKeyReason) => {
            setPayload({
                ...payload,
                keyReasons: payload.keyReasons.map(it =>
                    it.employeeKey.key.id === employeeKey.key.id && it.employeeKey.historyRecord.id === employeeKey.historyRecord.id
                        ? {employeeKey, reason}
                        : it
                )
            });
        },
    }), [setPayload, payload]);

    const expDateAdornment = useMemo(() => {
        return createDateFieldAdornment(handlers.setExpToDate, [
            {label: 'Today', date: () => today()},
            {label: 'Friday', date: () => isoDate(withDayOfWeek(new Date(), WeekDay.Friday))}
        ]);
    }, [handlers]);


    useEffect(() => {
        const keyReasons = payload.employee
            ? employeeKeyHistory.active(payload.employee).map(employeeKey => ({employeeKey, reason: undefined}))
            : [];
        setPayload(payload => ({...payload, keyReasons}));
    }, [payload.employee, setPayload, employeeKeyHistory]);

    return (
        <>
            <ToggleButtonGroup
                value={payload.personType}
                exclusive
                fullWidth
                onChange={handlers.handlePersonTypeChange}
            >
                <Tooltip title={
                    'Issue the key to a JetBrains employee travelling from another office or local employee who lost or forgot their key at home'
                }>
                    <ToggleButton value="employee">
                        <JetBrainsIcon fontSize="medium" sx={{mb: 0.5}}/>
                        Employee
                    </ToggleButton>
                </Tooltip>
                <Tooltip title={
                    'Issue the key to a person who is not a JetBrains employee but still allowed to have a key. Typically cleaning, catering, plants care, language teacher, regular vendor, or some other kind of contractor'
                }>
                    <ToggleButton value="guest">
                        <ContractorIcon fontSize="medium" sx={{mb: 0.5, mr: 0.5}}/>
                        Contractor
                    </ToggleButton>
                </Tooltip>
            </ToggleButtonGroup>

            {payload.personType === 'employee' ? (
                <>
                    <FormControl fullWidth required>
                        <EmployeeSelect
                            label="Select Employee"
                            employee={payload.employee}
                            setEmployee={handlers.setEmployee}
                            required
                            variant="outlined"
                        />
                    </FormControl>
                </>
            ) : (
                <>
                    <FormControl fullWidth required>
                        <TextField
                            label="Contractor Full Name"
                            value={payload.guestFullName}
                            onChange={handlers.handleGuestFullNameChange}
                            required
                            variant="outlined"
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <CompanySelect
                            label="Contractor Company"
                            value={payload.guestCompany || ""}
                            setValue={handlers.setGuestCompany}
                            variant="outlined"
                        />
                    </FormControl>
                </>
            )}

            <FormControl fullWidth>
                <ToggleButtonGroup
                    value={payload.issuanceType}
                    exclusive
                    fullWidth
                    onChange={handlers.handleIssuanceTypeChange}
                    aria-label="Key issue duration"
                >
                    <Tooltip title="Issue the key until an expected return date">
                        <ToggleButton value="temporary">Temporary</ToggleButton>
                    </Tooltip>
                    <Tooltip title="Issue the key without an expected return date">
                        <ToggleButton value="permanent">Permanent</ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>
            </FormControl>

            {payload.issuanceType === 'temporary' && (
                <DateField
                    label="Expected Return Date"
                    value={payload.expToDate}
                    setValue={handlers.setExpToDate}
                    slots={{inputAdornment: expDateAdornment}}
                    slotProps={{
                        field: {
                            clearable: true,
                        },
                        textField: {
                            required: true,
                            variant: 'outlined',
                        }
                    }}
                />
            )}

            {payload.keyReasons.length > 0 ? (
                <Stack direction="column" gap={2}>
                    {payload.keyReasons.map(keyReason => (
                        <KeyReasonSelect
                            key={`${keyReason.employeeKey.key.id}-${keyReason.employeeKey.historyRecord.id}`}
                            reason={keyReason.reason}
                            employeeKey={keyReason.employeeKey}
                            setReason={handlers.setReason}
                        />
                    ))}
                </Stack>
            ) : (<Box/>)}
        </>
    );
}

function SelectVaultSubpanel({payload, setPayload}: {
    payload: SelectVaultActionPayload,
    setPayload: SetState<SelectVaultActionPayload>,
}) {
    const handlers = useMemo(() => ({
        setTargetVault: mapSetState(setPayload, 'targetVault'),
    }), [setPayload]);

    return (
        <Stack direction="column" gap={2}>
            <ActionHeader text="Move to..."/>

            <FormControl fullWidth required>
                <KeyVaultSelect
                    label="Vault"
                    keyVault={payload.targetVault}
                    setKeyVault={handlers.setTargetVault}
                />
            </FormControl>
        </Stack>
    );
}

function validateTransactionDates(transactions: { keySnapshot: KeySnapshot, tx: KeyTransaction }[]) {
    const invalidTransaction = transactions.find(({keySnapshot, tx}) => {
        const from = keySnapshot.historyRecord.from;
        return !!from && tx.date < from;
    });

    if (invalidTransaction) {
        throw new ValidationError(
            `Operation date cannot be before the current record start date ${invalidTransaction.keySnapshot.historyRecord.from}.`,
            'date',
        );
    }
}

function UndoOperationAction({snackbarId, transactions}: {
    snackbarId: SnackbarKey,
    transactions: KeyTransactionRecord[],
}) {
    const message = useMessage();
    const rollback = useKeyTransactionStore(state => state.rollback);
    const {closeSnackbar} = useSnackbar();
    const [loading, setLoading] = useState(false);

    const handleUndo = useCallback(async () => {
        setLoading(true);
        closeSnackbar(snackbarId);
        try {
            for (const transaction of [...transactions].reverse()) {
                await rollback(transaction);
            }
            message.success('Operation rolled back successfully');
        } catch (error) {
            message.error('Failed to rollback operation', error);
        }
    }, [closeSnackbar, message, rollback, snackbarId, transactions]);

    return (
        <Button disabled={loading} onClick={handleUndo} variant="contained" color="success">
            Undo
        </Button>
    );
}

export function KeyOperationPanel({keySnapshot, onFinish}: {
    keySnapshot: KeySnapshot,
    onFinish: () => void,
}) {
    const message = useMessage();
    const executeTx = useKeyTransactionStore(state => state.execute);
    const [actionLoading, setActionLoading] = useState(false);
    const user = useAuthStore(state => state.user);
    const [actions, setActions] = useState<WizardKeyAction[]>([]);
    const [selectedAction, setSelectedAction] = useState<WizardKeyAction>(noopAction);
    const [error, setError] = useState<string | undefined>(undefined);
    const [notes, setNotes] = useState<string>('')

    useEffect(() => {
        const actions = getAvailableActions(keySnapshot);
        setActions(actions);
        setSelectedAction(actions[0] || noopAction);
        setError(undefined);
    }, [keySnapshot, setActions, setSelectedAction, setError]);

    const handleSelectedActionChange = useCallback((_event: SyntheticEvent<any>, newValue: WizardKeyAction['key']) => {
        setSelectedAction(actions.find(action => action.key === newValue) || noopAction);
        setError(undefined);
    }, [setSelectedAction, actions, setError]);

    const setPayload = useMemo(() => {
        return mapSetState(setSelectedAction, 'payload');
    }, [setSelectedAction]);

    const handleActionClick = useCallback(async () => {
        const payload = selectedAction.payload;
        if (keySnapshot === undefined) return;

        try {
            setError(undefined);
            setActionLoading(true);
            let result: {
                transactions: { keySnapshot: KeySnapshot, tx: KeyTransaction }[],
                successMessage: string,
                failureMessage: string,
            }
            const t = today();
            const f = keySnapshot.historyRecord.from;
            const date = f && f > t ? f : t

            switch (payload.type) {
                case "noop":
                    result = await payload.submit(user, keySnapshot, payload, date, notes);
                    break;
                case "select-person":
                    result = await payload.submit(user, keySnapshot, payload, date, notes);
                    break;
                case "select-vault":
                    result = await payload.submit(user, keySnapshot, payload, date, notes);
                    break;
                case "apply-directly":
                    result = await payload.submit(user, keySnapshot, payload, date, notes);
                    break;
            }

            validateTransactionDates(result.transactions);

            try {
                const transactions = await executeTx(result.transactions);
                onFinish();
                message.success(
                    result.successMessage,
                    snackbarId => <UndoOperationAction snackbarId={snackbarId} transactions={transactions}/>,
                    20_000,
                );
            } catch (error) {
                message.error(result.failureMessage, error);
            }
        } catch (e) {
            if (e instanceof ValidationError) {
                setError(e.message);
            } else {
                setError(apiErrorMessage(e) || 'Unknown error');
            }
        } finally {
            setActionLoading(false);
        }
    }, [user, message, selectedAction, keySnapshot, onFinish, notes, executeTx]);

    const handleNotesChange = useMemo(() => inputChangeEvent(setNotes), [setNotes]);

    return (
        <Box>
            <KeyCurrentStatePanel keySnapshot={keySnapshot}/>

            <Stack
                direction="row"
                gap={2}
                alignItems="baseline"
                sx={{mt: 4, ml: 2, borderBottom: 1, borderColor: 'divider', mb: 3}}
            >
                <Typography variant="h5">Available Key Actions</Typography>

                <Tabs
                    value={selectedAction?.key || ''}
                    onChange={handleSelectedActionChange}

                >
                    {actions.map(action => (
                        <Tab
                            key={action.key}
                            value={action.key}
                            label={(
                                <Tooltip title={action.tooltip}>
                                    <span>{action.label}</span>
                                </Tooltip>
                            )}
                            sx={{textAlign: "right"}}
                            // icon={createElement(action.icon)}
                            iconPosition="start"
                        />
                    ))}
                </Tabs>
            </Stack>

            <Grid container spacing={2} flexGrow={1} px={2}>
                <Grid size={{md: 6, xs: 12}} direction="column" gap={2} container justifyContent="space-between"
                      flexGrow={1}>
                    <Stack direction="column" gap={2}>
                        {selectedAction.payload.type === 'select-person' && (
                            <SelectPersonSubpanel
                                payload={selectedAction.payload}
                                setPayload={setPayload as SetState<SelectPersonActionPayload>}
                            />
                        )}
                        {selectedAction.payload.type === 'select-vault' && (
                            <SelectVaultSubpanel
                                payload={selectedAction.payload}
                                setPayload={setPayload as SetState<SelectVaultActionPayload>}
                            />
                        )}
                        {selectedAction.payload.type === 'apply-directly' && (
                            <ActionHeader text={selectedAction.payload.description}/>
                        )}

                        {keySnapshot && (
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Notes"
                                value={notes}
                                onChange={handleNotesChange}
                                variant="outlined"
                            />
                        )}
                    </Stack>

                    {keySnapshot && (
                        <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
                            <Grid container direction="row" spacing={2} flexGrow={1}>
                                {error && (
                                    <Grid flexGrow={1}>
                                        <Alert severity="error">{error}</Alert>
                                    </Grid>
                                )}
                            </Grid>
                            <Stack direction="row" gap={2} justifyContent="flex-end" sx={{my: 0.5}}>
                                <ControlWithProgress loading={actionLoading}>
                                    <Button
                                        variant="contained"
                                        onClick={handleActionClick}
                                        disabled={actionLoading}
                                    >
                                        {selectedAction.submitLabel}
                                    </Button>
                                </ControlWithProgress>
                            </Stack>
                        </Stack>
                    )}
                </Grid>
                <Grid size={{md: 6, xs: 12}}>
                    {selectedAction.payload.type === 'select-person' && selectedAction.payload.personType === 'employee' && selectedAction.payload.employee && (
                        <EmployeeCardMedia employee={selectedAction.payload.employee} height="400"/>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}
